"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import type { SkillCategoryItem } from "@/lib/clawhub-api"

interface SkillsCategoryFilterProps {
  /** tag 列表（含首位"全部"项），来自 fetchTags */
  tags: SkillCategoryItem[]
  selectedTag: string
  onTagClick: (tagName: string) => void
  onClearTag: () => void
}

const CAT_SCROLL_STEP_PX = 160

/**
 * SkillsCategoryFilter — horizontal scrollable category pills + active tag pill.
 * Manages its own scroll state (canScrollLeft/Right) as pure UI layout state.
 */
export function SkillsCategoryFilter({
  tags,
  selectedTag,
  onTagClick,
  onClearTag,
}: SkillsCategoryFilterProps) {
  const catScrollRef = useRef<HTMLDivElement>(null)
  const [catCanScrollLeft, setCatCanScrollLeft] = useState(false)
  const [catCanScrollRight, setCatCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = catScrollRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    const canScroll = scrollWidth > clientWidth + 2
    setCatCanScrollLeft(canScroll && scrollLeft > 2)
    setCatCanScrollRight(canScroll && scrollLeft < scrollWidth - clientWidth - 2)
  }, [])

  useEffect(() => {
    updateScrollState()
    const el = catScrollRef.current
    if (!el) return
    const t = setTimeout(updateScrollState, 100)
    el.addEventListener("scroll", updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      clearTimeout(t)
      el.removeEventListener("scroll", updateScrollState)
      ro.disconnect()
    }
  }, [selectedTag, updateScrollState])

  return (
    <div className="shrink-0 w-full max-w-[min(96vw,640px)] mx-auto px-4 sm:px-6 pt-3 pb-1">
      <div className="flex flex-row items-center justify-center gap-1 w-full">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center self-center">
          {catCanScrollLeft ? (
            <button
              type="button"
              aria-label="向左滑动分类"
              className="skills-scroll-arrow skills-scroll-arrow--md"
              onClick={() =>
                catScrollRef.current?.scrollBy({
                  left: -CAT_SCROLL_STEP_PX,
                  behavior: "smooth",
                })
              }
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
          ) : (
            <span className="block h-7 w-7" aria-hidden />
          )}
        </div>
        <div className="relative min-w-0 max-w-full flex-1 overflow-hidden rounded-lg">
          <div
            ref={catScrollRef}
            className="no-scrollbar overflow-x-auto overflow-y-hidden py-1 pl-1 pr-1 skills-scrollable"
          >
            <div className="flex flex-nowrap items-center gap-2 w-max">
              {tags.map((tag) => {
                // "全部"：无 tag 选中时高亮；其他：当前 selectedTag 匹配时高亮
                const active =
                  tag.value === "all"
                    ? !selectedTag
                    : selectedTag === tag.value
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => onTagClick(tag.value === "all" ? "" : tag.value)}
                    className={`skills-cat-pill ${active
                      ? "skills-cat-pill--active"
                      : "skills-cat-pill--inactive"
                      }`}
                  >
                    {tag.label || tag.value}
                    {tag.value !== "all" && tag.count > 0 && (
                      <span className="skills-tag-pill__count">{tag.count}</span>
                    )}
                  </button>
                )
              })}
              {/* 仅当 selectedTag 不在 tags 列表中（如从搜索框输入）时，显示独立 tag pill */}
              {selectedTag && !tags.some((t) => t.value === selectedTag) && (
                <span className="skills-active-tag">
                  #{selectedTag}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onClearTag()
                    }}
                    className="skills-active-tag__remove"
                    aria-label="清除标签筛选"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
          <div
            className={`skills-fade-gradient skills-fade-gradient--left ${catCanScrollLeft ? "opacity-100" : "opacity-0"
              }`}
            aria-hidden
          />
          <div
            className={`skills-fade-gradient skills-fade-gradient--right ${catCanScrollRight ? "opacity-100" : "opacity-0"
              }`}
            aria-hidden
          />
        </div>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center self-center">
          {catCanScrollRight ? (
            <button
              type="button"
              aria-label="向右滑动分类"
              className="skills-scroll-arrow skills-scroll-arrow--md"
              onClick={() =>
                catScrollRef.current?.scrollBy({
                  left: CAT_SCROLL_STEP_PX,
                  behavior: "smooth",
                })
              }
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <span className="block h-7 w-7" aria-hidden />
          )}
        </div>
      </div>
    </div>
  )
}
